import { useState, useEffect } from 'react';
import { getAuth, getStore } from "../firebase";
import { API_URL } from "../constants";
import { getDateTime } from "../utils/date";
import { deleteFileFromURL } from "./storage";
import { EventsQueries, unserializeEvent } from './event';

const EVENT_TYPES = "eventTypes";

export interface EventTypeDoc {
  id?: string, // the firebase document ID
  createdAt: string | Date,
  updatedAt: string | Date,
  slug: string, // e.g. "speed-dating"
  name: string, // e.g. "Speed Dating"
  pageTitle: string, // e.g. "Speed Dating in London", ` | True Dating` will be appended 
  metaDescription: string, 
  mainHeading: string, // the H1 on the page
  heroRichText: string, // the DraftEditor state for the hero section rich text

  eventSearchHeading: string, // the title of the event search page, e.g. "Speed Dating Events in London"

  imgMobileUrl: string,
  imgURL: string,
  
  initialSearchParams: EventsQueries,

  city?: string, // OPTIONAL city name, e.g. "London"
  description: string,
  howItWorks: {
    col1: {
      description: string
      imgURL: string
    }
    col2: {
      description: string
      imgURL: string
    }
    col3: {
      description: string
      imgURL: string
    }
    finalNotes: string
  }
}

export interface EventType extends EventTypeDoc{
  createdAt:  Date,
  updatedAt: Date,
}



export const blankEventType: EventType = {
  slug: '',
  createdAt: new Date(),
  updatedAt: new Date(),
  name: '',
  pageTitle: '',
  metaDescription: '',
  mainHeading: '',
  heroRichText: '',
  eventSearchHeading: '',
  city: '',
  description: '',
  imgURL: null,
  imgMobileUrl: null,

  initialSearchParams: {
    ageFrom: null,
    ageTo: null,
    location: null,
    type: null,
    date: null,
  },

  howItWorks: {
    col1: {
      imgURL: null,
      description: null,
    },
    col2: {
      imgURL: null,
      description: null,
    },
    col3: {
      imgURL: null,
      description: null,
    },
    finalNotes: null,
  },
}

export const getAllEventTypes = async () => {
  const { db, query, collection, orderBy, getDocs } = await getStore();
  const q = query(collection(db, EVENT_TYPES), orderBy("name", "asc"));
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map((doc) => {
    const docData = doc.data() as EventType;
    return {
      ...docData,
      id: doc.id,
    };
  });
};

export const getEventTypeById = async (id: string) => {
  const { db, doc, getDoc } = await getStore();
  const docRef = doc(db, EVENT_TYPES, id);
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    const docData = docSnap.data() as EventType;
    return {
      ...docData,
      id: docSnap.id,
    };
  } else {
    console.error("No such document!");
    return null;
  }
}

export const getEventTypeBySlug = async (slug: string): Promise<EventType> => {
  const { db, query, collection, where, getDocs } = await getStore();
  const q = query(collection(db, EVENT_TYPES), where("slug", "==", slug));
  const querySnapshot = await getDocs(q);
  if (querySnapshot.empty) {
    console.error("No such document!");
    return null;
  }
  const docData = querySnapshot.docs[0].data() as EventTypeDoc;
  return unserializeEventType({
    ...docData,
    id: querySnapshot.docs[0].id,
  });
}

export const createEventType = async (event) => {
  const { db, addDoc, collection } = await getStore();
  return addDoc(collection(db, EVENT_TYPES), {
    createdAt: new Date(),
    updatedAt: new Date(),
    ...event,
  });
}

export const updateEventType = async ({ id, ...eventType }) => {
  const { db, doc, updateDoc } = await getStore();
  return updateDoc(doc(db, EVENT_TYPES, id), {...eventType, updatedAt: new Date()});
}

export const useFetchEventTypes = (): [EventType[], boolean] => {
  const [eventTypes, setEventTypes] = useState<EventType[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    const loadEventTypes = async () => {
      setLoading(true);
      try {
        const eventTypes = await getAllEventTypes();
        if (eventTypes.length) {
          setEventTypes(eventTypes);
        }
      } catch (error) {
        console.error(error);
      }
      setLoading(false);
    }
    loadEventTypes();
  }, []);

  return [ eventTypes, loading ];

}

export const useFetchEventType = (id: string): [EventType, boolean] => {
  const [eventType, setEventType] = useState(blankEventType);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!id) return;
    const loadEvent = async () => {
      setLoading(true);
      try {
        const event = await getEventTypeById(id);
        if (event) {
          setEventType(event);
        }
      } catch (error) {
        console.error(error);
      }
      setLoading(false);
    }
    loadEvent();
  }, [id]);

  return [ eventType, loading ];
}

export const serializeEventType = (eventType: EventType): EventTypeDoc => {
  const { createdAt, updatedAt, ...rest } = eventType;
  return {
    ...rest,
    createdAt: createdAt.toString() ?? new Date().toString(),
    updatedAt: updatedAt?.toString() ?? new Date().toString(),
  }
}

export const unserializeEventType = (eventType: EventTypeDoc): EventType => {
  const { createdAt, updatedAt, ...rest } = eventType;
  return {
    ...rest,
    createdAt: new Date(createdAt),
    updatedAt: new Date (updatedAt),
  }
}